import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  toClient: {},
  fromClient: {},
  toEmergencyContact: {},
  fromEmergencyContact: {},
  toFirm: {},
  fromFirm: {},
  toFirmContact: {},
  fromFirmContact: {},
  toDl: {},
  outreachToCategoryMap: {},
  outreachToKeyMap: {},
};

const addOutreaches = (state, keyId, outreaches, category) => {
  const tempReverseMap = { ...state.outreachToCategoryMap };
  outreaches.forEach((o) => (tempReverseMap[o.outreachId] = category));

  const tempMapToKeys = { ...state.outreachToKeyMap };
  outreaches.forEach((o) => (tempMapToKeys[o.outreachId] = keyId));

  const newCategoryMap = { ...state[category] };
  if (!newCategoryMap.hasOwnProperty(keyId)) {
    newCategoryMap[keyId] = outreaches;
  }

  const currentList = newCategoryMap[keyId];
  const idsOfOutreachesToAdd = outreaches.map((o) => o.outreachId);
  const prunedList = currentList.filter((o) => !idsOfOutreachesToAdd.some((id) => id === o.outreachId));
  const newList = [...prunedList, ...outreaches];
  newCategoryMap[keyId] = newList;
  return {
    ...state,
    [category]: newCategoryMap,
    outreachToCategoryMap: tempReverseMap,
    outreachToKeyMap: tempMapToKeys,
  };
};

export const outreachesSlice = createSlice({
  name: 'outreaches',
  initialState: initialState,
  reducers: {
    clearOutreaches2: () => {
      return initialState;
    },

    addOutreachesToClient: (state, action) => {
      const { clientId, outreaches } = action.payload;
      return addOutreaches(state, clientId, outreaches, 'toClient');
    },

    addOutreachesFromClient: (state, action) => {
      const { clientId, outreaches } = action.payload;
      return addOutreaches(state, clientId, outreaches, 'fromClient');
    },

    addOutreachesToEmergencyContact: (state, action) => {
      const { contactId, outreaches } = action.payload;
      return addOutreaches(state, contactId, outreaches, 'toEmergencyContact');
    },

    addOutreachesFromEmergencyContact: (state, action) => {
      const { contactId, outreaches } = action.payload;
      return addOutreaches(state, contactId, outreaches, 'fromEmergencyContact');
    },

    addOutreachesToFirm: (state, action) => {
      const { firmId, outreaches } = action.payload;
      return addOutreaches(state, firmId, outreaches, 'toFirm');
    },

    addOutreachesFromFirm: (state, action) => {
      const { firmId, outreaches } = action.payload;
      return addOutreaches(state, firmId, outreaches, 'fromFirm');
    },

    addOutreachesToFirmContact: (state, action) => {
      const { contactId, outreaches } = action.payload;
      return addOutreaches(state, contactId, outreaches, 'toFirmContact');
    },

    addOutreachesFromFirmContact: (state, action) => {
      const { contactId, outreaches } = action.payload;
      return addOutreaches(state, contactId, outreaches, 'fromFirmContact');
    },

    addOutreachesToDl: (state, action) => {
      const { dlId, outreaches } = action.payload;
      return addOutreaches(state, dlId, outreaches, 'toDl');
    },

    removeFirmOutreach: (state, action) => {
      const outreachId = action.payload;
      const tempReverseMap = { ...state.outreachToCategoryMap };
      const tempMapToKeys = { ...state.outreachToKeyMap };
      const category = tempReverseMap[outreachId];
      const contactId = tempMapToKeys[outreachId];
      const mapForCategory = state[category];
      const listForContact = mapForCategory[contactId];
      const prunedList = listForContact.filter((o) => o.outreachId !== outreachId);
      const prunedMapForCategory = { ...mapForCategory, [contactId]: prunedList };
      delete tempReverseMap[outreachId];
      delete tempMapToKeys[outreachId];
      return {
        ...state,
        [category]: prunedMapForCategory,
        outreachToCategoryMap: tempReverseMap,
        outreachToKeyMap: tempMapToKeys,
      };
    },

    updateFirmOutreach: (state, action) => {
      const outreach = action.payload;
      const outreachId = outreach.outreachId;
      const category = state.outreachToCategoryMap[outreachId];
      const contactId = state.outreachToKeyMap[outreachId];
      const mapForCategory = state[category];
      const listForContact = mapForCategory[contactId];
      const prunedList = listForContact.filter((o) => o.outreachId !== outreachId);
      const updatedList = [...prunedList, outreach];
      return { ...state, [category]: updatedList };
    },
  },
});

export const {
  clearOutreaches2,
  addOutreachesToClient,
  addOutreachesFromClient,
  addOutreachesToEmergencyContact,
  addOutreachesFromEmergencyContact,
  addOutreachesToFirm,
  addOutreachesFromFirm,
  addOutreachesToFirmContact,
  addOutreachesFromFirmContact,
  addOutreachesToDl,
  removeFirmOutreach,
  updateFirmOutreach,
} = outreachesSlice.actions;

export default outreachesSlice.reducer;
